import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FolderKanban, Plus, X, MapPin, FileImage } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow, Spinner, StatusPill } from "@/components/tac";

const inp = "w-full rounded-xl border border-slate-700 bg-[#0d1420] px-4 py-3 text-sm text-white focus:border-amber-500 focus:outline-none";
const PHASE_LABEL = { pre_construction: "Pre-Construction", construction: "Construction", post_construction: "Post-Construction" };

export default function Projects() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["owner", "dispatcher"].includes(user?.role);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", gc_name: "" });

  const { data, isLoading } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });

  const create = async () => {
    try {
      await api.post("/projects", form);
      toast.success("Project created"); setOpen(false); setForm({ name: "", address: "", gc_name: "" });
      qc.invalidateQueries({ queryKey: ["projects"] });
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><Eyebrow>Full project lifecycle</Eyebrow><h1 className="mt-2 font-display text-3xl font-bold text-white">Projects</h1>
          <p className="mt-2 text-sm text-slate-400">Blueprints, pin plans and material takeoffs from pre-con to handover.</p></div>
        {canEdit && <button data-testid="new-project-btn" onClick={() => setOpen(true)} className="btn-amber inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" /> New project</button>}
      </div>

      {isLoading ? <div className="mt-10"><Spinner /></div> : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data || []).map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} data-testid={`project-card-${p.id}`}
              className="card-tac group p-5 transition-colors hover:border-amber-500/50">
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/10 text-amber-400"><FolderKanban className="h-5 w-5" /></div>
                <StatusPill status={p.status} />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-white group-hover:text-amber-400">{p.name}</h3>
              <p className="mt-1 text-xs text-slate-500">{PHASE_LABEL[p.status]}{p.gc_name ? ` · GC: ${p.gc_name}` : ""}</p>
              {p.address && <p className="mt-1 text-xs text-slate-500">{p.address}</p>}
              <div className="mt-4 flex items-center gap-4 font-mono-tac text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><FileImage className="h-3.5 w-3.5" /> {p.blueprint_count} blueprint{p.blueprint_count === 1 ? "" : "s"}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {p.pin_count} pins</span>
              </div>
            </Link>
          ))}
          {!(data || []).length && <div className="card-tac col-span-full flex flex-col items-center gap-3 py-16 text-center text-slate-500" data-testid="projects-empty"><FolderKanban className="h-8 w-8 text-slate-700" /> No projects yet.</div>}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <div className="card-tac w-full max-w-md space-y-3 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-semibold text-white">New project</h2><button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button></div>
            <input data-testid="project-name-input" placeholder="Project name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} />
            <input data-testid="project-address-input" placeholder="Site address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} />
            <input data-testid="project-gc-input" placeholder="General contractor (optional)" value={form.gc_name} onChange={(e) => setForm({ ...form, gc_name: e.target.value })} className={inp} />
            <button data-testid="project-submit" disabled={!form.name.trim()} onClick={create} className="btn-amber w-full rounded-xl py-3 text-sm disabled:opacity-50">Create project</button>
          </div>
        </div>
      )}
    </div>
  );
}
